import React, { FC, PropsWithChildren, useEffect } from 'react'
import { Box, SimpleCarousel, Flex } from '@moonpig/launchpad-components'
import { getContactUsSuggestions } from '@moonpig/web-core-app-shell-search'
import { useRouter } from '@moonpig/web-core-routing'
import { styled } from '@moonpig/launchpad-utils'
import {
  EventObject,
  SelectContentEvent,
  trackGAEvent,
  useTrackGAEventOnce,
} from '@moonpig/web-core-analytics'
import { useFindLocaleText } from '../../text-localisation'

const HoverDiv = styled.div`
  cursor: pointer;
`
const StyledAnchor = styled.a`
  text-decoration: none;
  color: inherit;
`

const CarouselButton: FC<
  PropsWithChildren<{
    url: string
    text: string
    searchTerm: string
    type: string
  }>
> = ({ url, children, text, searchTerm, type }) => {
  const handleClick = () => {
    const selectContentEvent: SelectContentEvent = {
      event: 'select_content',
      content_data: {
        content_type: `search | button clicked | contact us button | ${searchTerm} | ${type} | ${url}`,
        item_id: undefined,
      },
      event_data: {
        category: 'search',
        action: 'button clicked',
        label: `contact us button | ${searchTerm} | ${type} | ${url}`,
        non_interaction: true,
        value: undefined,
      },
      discount_data: undefined,
      ecommerce: undefined,
      error_data: undefined,
    }
    trackGAEvent(selectContentEvent)
  }

  return (
    <Box
      borderRadius={2}
      mr={6}
      border={1}
      borderColor={'colorBorder03'}
      bgcolor={'colorBlack10'}
      overflow={'hidden'}
      width={'148px'}
    >
      <HoverDiv>
        <StyledAnchor href={url} onClick={handleClick}>
          <Flex width={'100%'} justifyContent={'center'} padding={4}>
            {children}
          </Flex>
          <Box
            bgcolor={'colorBackground01'}
            textAlign={'center'}
            padding={4}
            width={'148px'}
            typography={'typeButtonLabel'}
          >
            {text}
          </Box>
        </StyledAnchor>
      </HoverDiv>
    </Box>
  )
}

export const ContactUsCarousel: FC<{ searchTerm: string }> = ({
  searchTerm,
}) => {
  const router = useRouter()
  const { params } = router.getCurrentRoute<'search'>()
  const { region } = params
  const navigationalSuggestions = getContactUsSuggestions(searchTerm, region)
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const t = useFindLocaleText()

  useEffect(() => {
    const impressionEvent: EventObject = {
      event: 'impression',
      content_data: {
        content_type: `search | view | contact us carousel | ${searchTerm}`,
      },
      event_data: {
        category: 'search',
        action: 'view',
        label: `contact us carousel | ${searchTerm}`,
        non_interaction: true,
        value: undefined,
      },
    }
    if (navigationalSuggestions && navigationalSuggestions.length > 0) {
      trackGAEventOnce(impressionEvent)
    }
  }, [navigationalSuggestions, searchTerm, trackGAEventOnce])

  if (!navigationalSuggestions || navigationalSuggestions.length === 0) {
    return <></>
  }

  return (
    <Box
      width={'100%'}
      mx={{ md: -6 }}
      mt={6}
      mb={6}
      data-testid={'contact-us-carousel'}
    >
      <SimpleCarousel gap={0} aria-label={t('find.contact_us_carousel')}>
        {navigationalSuggestions.map(x => (
          <CarouselButton
            url={x.url(region)}
            text={x.displayText}
            key={x.type}
            searchTerm={searchTerm}
            type={x.type}
          >
            {x.icon({ width: '46px', height: '46px' })}
          </CarouselButton>
        ))}
      </SimpleCarousel>
    </Box>
  )
}
